import type { ReactElement } from "react";

export interface DayWindowView {
  readonly date: string;
  readonly expectedBets: number;
  readonly expectedStakeCents: number;
  readonly sports: readonly {
    readonly sport: string;
    readonly games: number;
    readonly firstTip: string;
    readonly lastTip: string;
    readonly expectedBets: number;
    readonly expectedStakeCents: number;
  }[];
}

const SPORT_LABEL: Readonly<Record<string, string>> = { SOCCER: "EPL" };

const dayLabel = (date: string) =>
  new Intl.DateTimeFormat("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));

const bets = (value: number) =>
  value === 0 ? "no bets" : `≈ ${String(Math.round(value))} bets`;

/**
 * The week ahead (Moss, 2 Nov 2026): games per sport per Melbourne day, when
 * they tip, and the bets and money to expect at the current rate.
 */
export function CalendarPage({
  days = [],
}: {
  readonly days?: readonly DayWindowView[];
}): ReactElement {
  const busiest = Math.max(1, ...days.map((day) => day.expectedBets));
  return (
    <div className="calendar-page">
      <header className="page-heading">
        <h2>Week ahead</h2>
        <p>
          Expected bets use each sport&apos;s staked bets per game over the last
          fourteen days. Times are Melbourne.
        </p>
      </header>
      {days.length === 0 ? (
        <p className="tile-empty">Loading the schedule…</p>
      ) : (
        <ol className="calendar-days">
          {days.map((day) => (
            <li className="card calendar-day" key={day.date}>
              <header>
                <h3>{dayLabel(day.date)}</h3>
                <strong>
                  {bets(day.expectedBets)}
                  {day.expectedStakeCents > 0
                    ? ` · $${String(Math.round(day.expectedStakeCents / 100))}`
                    : ""}
                </strong>
              </header>
              <div className="calendar-load" aria-hidden="true">
                <span
                  style={{ width: `${(day.expectedBets / busiest) * 100}%` }}
                />
              </div>
              {day.sports.length === 0 ? (
                <small>No games.</small>
              ) : (
                <ul>
                  {day.sports.map((sport) => (
                    <li key={sport.sport}>
                      <b>{SPORT_LABEL[sport.sport] ?? sport.sport}</b>
                      <span>
                        {`${String(sport.games)} ${sport.games === 1 ? "game" : "games"} · tips ${sport.firstTip}${sport.lastTip === sport.firstTip ? "" : `–${sport.lastTip}`}`}
                      </span>
                      <span className="calendar-bets">
                        {bets(sport.expectedBets)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
